import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import NamedEntity from "../interfaces/NamedEntity";

export enum UserRole {
	Admin = "Admin",
	StateAdmin = "StateAdmin",
	DistrictAdmin = "DistrictAdmin",
	SchoolAdmin = "Principal",
	Proctor = "Proctor",
	Writer = "Writer",
	Learner = "SelfServiceWriter",
}

export const roleDescriptions: Record<UserRole, string> = {
	[UserRole.Admin]: "Super Administrator",
	[UserRole.StateAdmin]: "State Administrator",
	[UserRole.DistrictAdmin]: "District Administrator",
	[UserRole.SchoolAdmin]: "School Administrator",
	[UserRole.Proctor]: "Teacher",
	[UserRole.Writer]: "Student",
	[UserRole.Learner]: "Independent Learner",
};

export const AdminRoles: UserRole[] = [
	UserRole.Admin,
	UserRole.StateAdmin,
	UserRole.DistrictAdmin,
	UserRole.SchoolAdmin,
];

export const isDistrictOrSchoolAdmin = (userRole: UserRole) => {
	return userRole === UserRole.SchoolAdmin || userRole === UserRole.DistrictAdmin;
};

export const isHigherThanDistrictAdmin = (userRole: UserRole) => {
	return userRole === UserRole.StateAdmin || userRole === UserRole.Admin;
};

export const isAdminRole = (userRole?: UserRole | null) => {
	return userRole && AdminRoles.includes(userRole);
};

export interface Accessibility {
	fontSize: string | null;
	colorContrast: string | null;
	tintOverlay: string | null;
	lineReader: string | null;
}

export const nullAccessibility: Accessibility = {
	fontSize: null,
	colorContrast: null,
	tintOverlay: null,
	lineReader: null,
};

export interface User {
	userId: number | null;
	username: string | null;
	firstName: string | null;
	lastName: string | null;
	initials: string | null;
	role: UserRole | null;
	isTeachersWriterAccount: boolean | null;
	requiresAccountConfirmation: boolean | null;
	bypassTermsOfService: boolean | null;
	isImpersonating: boolean | null;
	userImageUploadDisabled: boolean | null;
	hasImage: boolean | null;
	accessibility: Accessibility | null;
}

const nullUser: User = {
	userId: null,
	username: null,
	firstName: null,
	lastName: null,
	initials: null,
	role: null,
	isTeachersWriterAccount: null,
	requiresAccountConfirmation: null,
	bypassTermsOfService: null,
	isImpersonating: null,
	userImageUploadDisabled: null,
	hasImage: null,
	accessibility: null,
};

export interface CourseState {
	id: number;
	name: string;
	gradeId: number;
	isActive: boolean;
	showUserAvatars?: boolean;
	isFavorite?: boolean;
}

export interface UserState extends User {
	isLoggedIn: boolean;
	isCourseInTransition: boolean;
	isSchoolInTransition: boolean;
	isDistrictInTransition: boolean;
	imageHasChanged: boolean;
	courses: CourseState[] | null;
	course: CourseState | null;
	schools: NamedEntity[] | null;
	school: NamedEntity | null;
	districts: NamedEntity[] | null;
	district: NamedEntity | null;
}

const initialState: UserState = {
	...nullUser,
	isLoggedIn: false,
	isCourseInTransition: false,
	isSchoolInTransition: false,
	isDistrictInTransition: false,
	imageHasChanged: false,
	courses: null,
	course: null,
	schools: null,
	school: null,
	districts: null,
	district: null,
};

export const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		resetUserState(state) {
			Object.assign(state, initialState);
		},
		setIsLoggedIn(state, action: PayloadAction<boolean>) {
			state.isLoggedIn = action.payload;
		},
		setUser(state, action: PayloadAction<User>) {
			state.userId = action.payload.userId;
			state.username = action.payload.username;
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
			state.initials = action.payload.initials;
			state.userImageUploadDisabled = action.payload.userImageUploadDisabled;
			state.hasImage = action.payload.hasImage;
			state.role = action.payload.role;
			state.isTeachersWriterAccount = action.payload.isTeachersWriterAccount;
			state.requiresAccountConfirmation = action.payload.requiresAccountConfirmation;
			state.bypassTermsOfService = action.payload.bypassTermsOfService;
			state.isImpersonating = action.payload.isImpersonating;
			state.accessibility = action.payload.accessibility;
		},
		setHasImage(state, action: PayloadAction<boolean>) {
			state.hasImage = action.payload;
			state.imageHasChanged = true;
		},
		setImageHasChanged(state, action: PayloadAction<boolean>) {
			state.imageHasChanged = action.payload;
		},
		setAccessibility(state, action: PayloadAction<Accessibility>) {
			state.accessibility = action.payload;
		},
		clearAccessibility(state) {
			state.accessibility = null;
		},
		setCourses(state, action: PayloadAction<CourseState[]>) {
			state.courses = action.payload;
		},
		setCourse(state, action: PayloadAction<CourseState>) {
			state.isCourseInTransition = false;
			state.course = { ...action.payload };
		},
		transitionCourse(state) {
			state.isCourseInTransition = true;
			state.course = null;
		},
		clearCourse(state) {
			state.isCourseInTransition = false;
			state.course = null;
		},
		setSchools(state, action: PayloadAction<NamedEntity[]>) {
			state.schools = action.payload;
		},
		setSchool(state, action: PayloadAction<NamedEntity | null>) {
			state.isSchoolInTransition = false;
			state.school = action.payload;
		},
		transitionSchool(state) {
			state.isSchoolInTransition = true;
			state.school = null;
		},
		clearSchool(state) {
			state.isSchoolInTransition = false;
			state.school = null;
		},
		setDistricts(state, action: PayloadAction<NamedEntity[]>) {
			state.districts = action.payload;
		},
		setDistrict(state, action: PayloadAction<NamedEntity | null>) {
			state.isDistrictInTransition = false;
			state.district = action.payload;
		},
		transitionDistrict(state) {
			state.isDistrictInTransition = true;
			state.district = null;
		},
		clearDistrict(state) {
			state.isDistrictInTransition = false;
			state.district = null;
		},
	},
});

export const {
	resetUserState,
	setIsLoggedIn,
	setUser,
	setHasImage,
	setImageHasChanged,
	setAccessibility,
	clearAccessibility,
	setCourses,
	setCourse,
	transitionCourse,
	clearCourse,
	setSchools,
	setSchool,
	transitionSchool,
	clearSchool,
	setDistricts,
	setDistrict,
	transitionDistrict,
	clearDistrict,
} = userSlice.actions;

export default userSlice.reducer;
